import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { fmtDate, fmtMoney } from "@/lib/format";

// Public, no-login customer page reached from the proforma email deeplink. The
// signed token in the path is the only credential. Reuses the partner-portal
// visual style. Lives OUTSIDE the _shell auth guard.
export const Route = createFileRoute("/p/proforma/$token")({
  component: PublicProformaPage,
});

interface PublicLine {
  section: string;
  kind: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  amountCents: number;
}
interface PublicProforma {
  number: string;
  title: string;
  partyName: string;
  locationText: string | null;
  periodStart: string | null;
  period: { from: string; to: string } | null;
  status: string;
  canAct: boolean;
  currency: string;
  subtotalCents: number;
  vatTotalCents: number;
  totalCents: number;
  reference: string | null;
  rejectionReason: string | null;
  lines: PublicLine[];
}

type State =
  | { k: "loading" }
  | { k: "error"; code: string; message: string }
  | { k: "loaded"; p: PublicProforma }
  | { k: "done"; result: "APPROVED" | "REJECTED" };

const GREEN = "var(--green-700)";

async function apiJson(url: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(url, init);
  const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
  if (!res.ok) {
    throw { code: body.error?.code ?? "ERROR", message: body.error?.message ?? "Er ging iets mis." };
  }
  return body;
}

const STATUS_LABEL: Record<string, string> = {
  APPROVED: "goedgekeurd",
  REJECTED: "afgewezen",
  INVOICED: "afgerond",
  DRAFT: "in bewerking",
  SENT: "verstuurd",
};

// The words the PDF uses, so the page and the attachment agree. The week only
// when no activity is dated.
function periodText(p: PublicProforma): string | null {
  if (p.period) {
    return p.period.from === p.period.to
      ? `datum ${fmtDate(p.period.from)}`
      : `periode ${fmtDate(p.period.from)} t/m ${fmtDate(p.period.to)}`;
  }
  return p.periodStart ? `week van ${fmtDate(p.periodStart)}` : null;
}

function PublicProformaPage() {
  const { token } = Route.useParams();
  const initialMode = new URLSearchParams(window.location.search).get("a");
  const [state, setState] = useState<State>({ k: "loading" });
  const [mode, setMode] = useState<"approve" | "reject" | null>(
    initialMode === "approve" ? "approve" : initialMode === "reject" ? "reject" : null,
  );
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiJson(`/api/v1/public/proforma/${encodeURIComponent(token)}`)
      .then((p) => {
        if (!alive) return;
        setState({ k: "loaded", p: p as PublicProforma });
        // A reference the project lead filled in at send: pre-filled, still editable.
        if ((p as PublicProforma).status === "SENT") setReference((p as PublicProforma).reference ?? "");
      })
      .catch((e: { code: string; message: string }) => alive && setState({ k: "error", ...e }));
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async (action: "approve" | "reject") => {
    setFormError(null);
    const body = action === "approve" ? { reference: reference.trim() } : { reason: reason.trim() };
    if (action === "approve" && !body.reference) return setFormError("Vul uw inkoop-/referentienummer in.");
    if (action === "reject" && !("reason" in body && body.reason)) return setFormError("Vul een reden in.");
    setSubmitting(true);
    try {
      await apiJson(`/api/v1/public/proforma/${encodeURIComponent(token)}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setState({ k: "done", result: action === "approve" ? "APPROVED" : "REJECTED" });
    } catch (e) {
      setFormError((e as { message?: string }).message ?? "Er ging iets mis.");
    } finally {
      setSubmitting(false);
    }
  };

  const partyName =
    state.k === "loaded" ? state.p.partyName : undefined;

  return (
    <div data-screen-label="Klantportaal — Proforma" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--surface-sunken, #f3f4f1)" }}>
      <header style={{ height: 60, background: GREEN, display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "0 var(--space-6)", color: "var(--gray-0)" }}>
        <img src="/mark-white.png" alt="" aria-hidden style={{ width: 32, height: 32, objectFit: "contain" }} />
        <div style={{ fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-md)" }}>Albert van de Scheur</div>
        <div style={{ flex: 1 }} />
        {partyName && <span style={{ fontSize: "var(--fs-sm)", color: "rgba(255,255,255,0.85)" }}>{partyName}</span>}
      </header>

      <main style={{ flex: 1, maxWidth: 720, width: "100%", margin: "0 auto", padding: "var(--space-8) var(--space-6) var(--space-16)" }}>
        {state.k === "loading" && <p style={{ color: "var(--text-secondary)" }}>Laden…</p>}

        {state.k === "error" && (
          <MessageCard
            tone={state.code === "LINK_EXPIRED" || state.code === "LINK_SUPERSEDED" ? "warn" : "danger"}
            title={
              state.code === "LINK_EXPIRED"
                ? "Deze link is verlopen"
                : state.code === "LINK_SUPERSEDED"
                  ? "Deze link is niet meer geldig"
                  : "Deze link is niet geldig"
            }
            body={
              state.code === "LINK_EXPIRED" || state.code === "LINK_SUPERSEDED"
                ? "Gebruik de link uit de meest recente e-mail met de nacalculatie, of reageer op die e-mail om uw akkoord of afwijzing door te geven."
                : "We konden deze nacalculatie niet openen. Controleer de link uit de e-mail of neem contact met ons op."
            }
          />
        )}

        {state.k === "done" && (
          <MessageCard
            tone={state.result === "APPROVED" ? "success" : "neutral"}
            title={state.result === "APPROVED" ? "Bedankt — uw akkoord is geregistreerd" : "Uw afwijzing is doorgegeven"}
            body={
              state.result === "APPROVED"
                ? "We verwerken uw akkoord en gaan voor u aan de slag. U ontvangt hiervan bevestiging."
                : "We hebben uw afwijzing ontvangen en nemen indien nodig contact met u op."
            }
          />
        )}

        {state.k === "loaded" && (
          <>
            <h1 className="t-h2">Nacalculatie {state.p.number}</h1>
            <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-md)", marginTop: 2 }}>{state.p.title}</p>

            <div className="avds-card" style={{ marginTop: "var(--space-5)" }}>
              <div className="avds-card__body">
                <div style={{ fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>
                  {[state.p.partyName, state.p.locationText, periodText(state.p)].filter(Boolean).join(" · ")}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--space-4)", fontSize: "var(--fs-sm)" }}>
                  <span style={{ color: "var(--text-secondary)" }}>Subtotaal (excl. btw)</span>
                  <span className="mono">{fmtMoney(state.p.subtotalCents, state.p.currency)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>
                  <span>Btw</span>
                  <span className="mono">{fmtMoney(state.p.vatTotalCents, state.p.currency)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--space-2)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-default)", fontWeight: "var(--fw-semibold)" }}>
                  <span>Totaal (incl. btw)</span>
                  <span className="mono">{fmtMoney(state.p.totalCents, state.p.currency)}</span>
                </div>
                <div style={{ marginTop: "var(--space-4)" }}>
                  <a href={`/api/v1/public/proforma/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noreferrer" className="avds-btn avds-btn--secondary avds-btn--sm">
                    Bekijk nacalculatie (PDF)
                  </a>
                </div>
              </div>
            </div>

            {!state.p.canAct ? (
              <MessageCard
                tone={state.p.status === "APPROVED" || state.p.status === "INVOICED" ? "success" : "neutral"}
                title={`Deze nacalculatie is al ${STATUS_LABEL[state.p.status] ?? "afgehandeld"}`}
                body={
                  state.p.status === "APPROVED" && state.p.reference
                    ? `Akkoord geregistreerd met referentie ${state.p.reference}.`
                    : state.p.status === "REJECTED" && state.p.rejectionReason
                      ? `Afgewezen: ${state.p.rejectionReason}`
                      : "Er is geen actie meer nodig."
                }
                style={{ marginTop: "var(--space-5)" }}
              />
            ) : (
              <div className="avds-card" style={{ marginTop: "var(--space-5)" }}>
                <div className="avds-card__body">
                  <div className="avds-hstack" style={{ gap: "var(--space-2)" }}>
                    <button className={`avds-btn ${mode === "approve" ? "avds-btn--primary" : "avds-btn--secondary"}`} onClick={() => { setMode("approve"); setFormError(null); }}>
                      Goedkeuren
                    </button>
                    <button className={`avds-btn ${mode === "reject" ? "avds-btn--danger" : "avds-btn--secondary"}`} onClick={() => { setMode("reject"); setFormError(null); }}>
                      Afwijzen
                    </button>
                  </div>

                  {mode === "approve" && (
                    <div className="avds-field" style={{ marginTop: "var(--space-4)" }}>
                      <label className="avds-field__label">Uw inkoop-/referentienummer</label>
                      <input className="avds-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="bijv. PO-123456" />
                      <div className="avds-field__help">
                        {state.p.reference
                          ? "Door ons alvast ingevuld. Klopt het niet? Pas het dan hier aan. Dit nummer koppelen we terug voor de facturatie."
                          : "Verplicht. Dit nummer koppelen we terug voor de facturatie."}
                      </div>
                      <button className="avds-btn avds-btn--primary" style={{ marginTop: "var(--space-3)" }} disabled={submitting} onClick={() => submit("approve")}>
                        Bevestig akkoord
                      </button>
                    </div>
                  )}

                  {mode === "reject" && (
                    <div className="avds-field" style={{ marginTop: "var(--space-4)" }}>
                      <label className="avds-field__label">Reden van afwijzing</label>
                      <textarea className="avds-textarea" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <div className="avds-field__help">Verplicht. We nemen indien nodig contact met u op.</div>
                      <button className="avds-btn avds-btn--danger" style={{ marginTop: "var(--space-3)" }} disabled={submitting} onClick={() => submit("reject")}>
                        Bevestig afwijzing
                      </button>
                    </div>
                  )}

                  {formError && (
                    <div className="avds-alert avds-alert--danger" style={{ marginTop: "var(--space-3)" }}>
                      {formError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <footer style={{ background: GREEN, color: "rgba(255,255,255,0.7)", fontSize: "var(--fs-sm)", padding: "var(--space-5) var(--space-6)", textAlign: "center" }}>
        Albert van de Scheur · Apeldoorn · © 2026
      </footer>
    </div>
  );
}

function MessageCard({
  tone,
  title,
  body,
  style,
}: {
  tone: "success" | "warn" | "danger" | "neutral";
  title: string;
  body: string;
  style?: React.CSSProperties;
}) {
  const border =
    tone === "success" ? "var(--green-500)" : tone === "warn" ? "var(--gold-300)" : tone === "danger" ? "var(--red-400, #d9534f)" : "var(--border-default)";
  return (
    <div className="avds-card" style={{ borderTop: `3px solid ${border}`, ...style }}>
      <div className="avds-card__body">
        <div className="t-h3" style={{ marginBottom: "var(--space-2)" }}>{title}</div>
        <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-md)" }}>{body}</p>
      </div>
    </div>
  );
}
